import { useState } from "react";
import { useCreateHarvest, useDeleteHarvest } from "../hooks/useGrowsQuery.js";
import useTimezone from "../hooks/useTimezone.js";
import { formatDate } from "../utility/datetime.js";

function nextFlushNumber(harvests) {
  if (!harvests.length) return 1;
  return Math.max(...harvests.map((h) => h.flush_number)) + 1;
}

function HarvestRow({ harvest, growId, tz }) {
  const deleteHarvest = useDeleteHarvest(growId);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <tr className="border-t border-gray-100 group">
      <td className="py-2.5 px-3 text-sm font-medium text-gray-700">
        Flush {harvest.flush_number}
      </td>
      <td className="py-2.5 px-3 text-sm text-gray-600">
        {formatDate(harvest.harvested_at, tz)}
      </td>
      <td className="py-2.5 px-3 text-sm text-gray-700 font-medium tabular-nums">
        {harvest.weight_grams != null ? `${harvest.weight_grams}g` : "—"}
      </td>
      <td className="py-2.5 px-3 text-sm text-gray-500">
        {harvest.notes ?? "—"}
      </td>
      <td className="py-2.5 px-3 text-right">
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            className="text-xs text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            Delete
          </button>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-gray-500">Sure?</span>
            <button
              onClick={() => deleteHarvest.mutate(harvest.id)}
              disabled={deleteHarvest.isPending}
              className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
            >
              {deleteHarvest.isPending ? "…" : "Yes"}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              No
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function HarvestLog({ growId, harvests = [] }) {
  const createHarvest = useCreateHarvest(growId);
  const tz = useTimezone();

  const [weight, setWeight] = useState("");
  const [notes, setNotes] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  const totalWeight = harvests.reduce((sum, h) => sum + (h.weight_grams ?? 0), 0);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    const body = { flush_number: nextFlushNumber(harvests) };
    if (weight) body.weight_grams = parseFloat(weight);
    if (notes.trim()) body.notes = notes.trim();
    try {
      await createHarvest.mutateAsync(body);
      setWeight("");
      setNotes("");
      setShowForm(false);
    } catch {
      setError("Failed to save harvest");
    }
  }

  return (
    <div className="bg-white rounded-xl shadow p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Harvest Log</h2>
          {harvests.length > 0 && (
            <div className="text-xs text-gray-400 mt-0.5">
              {harvests.length} flush{harvests.length !== 1 ? "es" : ""}
              {totalWeight > 0 && ` · ${totalWeight.toFixed(1)}g total`}
            </div>
          )}
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="text-xs bg-cyan-600 text-white rounded px-3 py-1.5 hover:bg-cyan-700 transition-colors font-medium"
          >
            + Log harvest
          </button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-4 bg-gray-50 rounded-lg p-4 flex flex-col gap-3">
          <div className="text-sm font-medium text-gray-600">
            Flush {nextFlushNumber(harvests)}
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Weight (grams)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="e.g. 142.5"
                className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Good pins, slightly dry"
                className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
          {error && <div className="text-xs text-red-600">{error}</div>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={createHarvest.isPending}
              className="text-xs bg-cyan-600 text-white rounded px-3 py-1.5 hover:bg-cyan-700 transition-colors disabled:opacity-50"
            >
              {createHarvest.isPending ? "Saving…" : "Save harvest"}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setWeight(""); setNotes(""); setError(null); }}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      {harvests.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-6">
          No harvests logged yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="pb-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Flush</th>
                <th className="pb-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Date</th>
                <th className="pb-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Weight</th>
                <th className="pb-2 px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Notes</th>
                <th className="pb-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {harvests.map((h) => (
                <HarvestRow key={h.id} harvest={h} growId={growId} tz={tz} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
